import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackContactSubmit } from "@/utils/analytics";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Loader2, Instagram, Facebook, Twitter } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Failed to send message");

      toast.success("Message sent! We'll get back to you soon.");
      trackContactSubmit();
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Contact error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 bg-[#F4F4F4]"
      data-testid="contact-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#0A1A2F]/10 text-[#0A1A2F] px-4 py-2 rounded text-sm font-bold uppercase tracking-wide mb-4 font-['Montserrat']">
            <Mail size={16} />
            We're Here to Help
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A1A2F] mb-4 font-['Montserrat']">
            Get In <span className="text-[#D4A857]">Touch</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-['Lato']">
            Have a question? We'd love to hear from you!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-[#0A1A2F] mb-4 font-['Montserrat']">
                Fighting Fit Fencing Club
              </h3>
              <p className="text-gray-600 leading-relaxed font-['Lato']">
                An informal and friendly fencing club based in London. We offer classes 
                for ages 6-16, from complete beginners to competitive fencers.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              <a
                href="tel:+442036375707"
                className="flex items-center gap-4 bg-white rounded-lg p-5 shadow-sm hover:shadow-md transition-all group border border-[#F4F4F4]"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-[#D4A857] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="text-[#0A1A2F]" size={22} />
                </div>
                <div>
                  <p className="font-bold text-[#0A1A2F] font-['Montserrat']">Phone</p>
                  <p className="text-gray-600 font-['Lato']">020 3637 5707</p>
                </div>
              </a>

              <a
                href="mailto:admin@ukswordfencing.com"
                className="flex items-center gap-4 bg-white rounded-lg p-5 shadow-sm hover:shadow-md transition-all group border border-[#F4F4F4]"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-[#0A1A2F] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="text-white" size={22} />
                </div>
                <div>
                  <p className="font-bold text-[#0A1A2F] font-['Montserrat']">Email</p>
                  <p className="text-gray-600 font-['Lato']">admin@ukswordfencing.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4 bg-white rounded-lg p-5 shadow-sm border border-[#F4F4F4]">
                <div className="w-12 h-12 bg-[#3FA9F5] rounded-lg flex items-center justify-center">
                  <MapPin className="text-white" size={22} />
                </div>
                <div>
                  <p className="font-bold text-[#0A1A2F] font-['Montserrat']">Location</p>
                  <p className="text-gray-600 font-['Lato']">Dedicated fencing venue, Barnet EN5 4DJ</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <p className="font-bold text-[#0A1A2F] mb-4 font-['Montserrat']">Follow Us</p>
              <div className="flex gap-4">
                <a
                  href="https://instagram.com/fightingfitfencing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#0A1A2F] rounded-lg flex items-center justify-center text-white hover:bg-[#D4A857] hover:text-[#0A1A2F] transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram size={22} />
                </a>
                <a
                  href="https://facebook.com/fightingfitfencing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#0A1A2F] rounded-lg flex items-center justify-center text-white hover:bg-[#D4A857] hover:text-[#0A1A2F] transition-colors"
                  data-testid="social-facebook"
                >
                  <Facebook size={22} />
                </a>
                <a
                  href="https://twitter.com/fightingfitfencing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-[#0A1A2F] rounded-lg flex items-center justify-center text-white hover:bg-[#D4A857] hover:text-[#0A1A2F] transition-colors"
                  data-testid="social-twitter"
                >
                  <Twitter size={22} />
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-8 border border-[#F4F4F4]">
              <h3 className="text-xl font-bold text-[#0A1A2F] mb-6 font-['Montserrat']">
                Send Us a Message
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="contact_name" className="font-['Montserrat']">Your Name *</Label>
                  <Input
                    id="contact_name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="mt-1"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email" className="font-['Montserrat']">Email Address *</Label>
                  <Input
                    id="contact_email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="mt-1"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone" className="font-['Montserrat']">Phone Number</Label>
                  <Input
                    id="contact_phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number (optional)"
                    className="mt-1"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_message" className="font-['Montserrat']">Message *</Label>
                  <Textarea
                    id="contact_message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="How can we help you?"
                    className="mt-1"
                    rows={4}
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0A1A2F] hover:bg-[#0D2240] text-white font-bold py-3 rounded transition-all font-['Montserrat'] uppercase tracking-wide"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
